import axios from "axios";
import { useSelector } from "react-redux";
import { useQuery, useInfiniteQuery } from "@tanstack/react-query";
import { API } from "../../config";
import { useMutation, useQueryClient } from "@tanstack/react-query";

const fetchVacAppointments = async (queryKey) => {
    const { userInfo, page, limit, status, date, patient } = queryKey;

    if (!userInfo._id)
        return { appointment: [], totalPages: 0, todayAppointment: [] }; // Handle case where user is not logged in

    const queryParams = new URLSearchParams();
    if (page) queryParams.append("page", page);
    if (limit) queryParams.append("limit", limit);
    if (status) queryParams.append("status", status);
    if (date) queryParams.append("date", date);
    if (patient) queryParams.append("patient", patient);

    const { data } = await axios.get(
        `${API}/vaccine-app-list/${userInfo._id}?${queryParams.toString()}`,
        {
            headers: {
                Authorization: `Bearer ${userInfo.token}`, // Get token from localStorage
            },
        }
    );

    return data;
};

const createVacApp = async (app, userInfo) => {
    const config = {
        headers: {
            Authorization: `Bearer ${userInfo.token}`,
        },
    };

    const { data } = await axios.post(
        `${API}/vaccine-app-create/${userInfo._id}`,
        app,
        config
    );

    return data;
};

export const useCreateVacApp = () => {
    const { userInfo } = useSelector((state) => state.userLogin);
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (app) => createVacApp(app, userInfo),
        onSuccess: (data) => {
            // Invalidate related queries to refresh list
            queryClient.invalidateQueries({
                queryKey: ["vaccineAppointments"],
            });
        },
        onError: (error) => {
            console.error(
                "Error creating vaccine appointment:",
                error.response?.data?.error || error.message
            );
        },
    });
};

const listAppointment = async ({ queryKey }) => {
    const [_, userInfo, page, limit, status, gtdate, ltdate, patient] =
        queryKey;

    if (!userInfo?._id) {
        return { appointment: [], totalPages: 0, todayAppointment: [] };
    }

    const queryParams = new URLSearchParams();
    if (page) queryParams.append("page", page);
    if (limit) queryParams.append("limit", limit);
    if (status) queryParams.append("status", status);
    if (gtdate) queryParams.append("gtdate", gtdate);
    if (ltdate) queryParams.append("ltdate", ltdate);
    if (patient) queryParams.append("patient", patient);

    const { data } = await axios.get(
        `${API}/vaccine-app-list/${userInfo._id}?${queryParams.toString()}`,
        {
            headers: {
                Authorization: `Bearer ${userInfo.token}`,
            },
        }
    );

    return data;
};

export const useAppointments = ({
    page = null,
    limit = null,
    status = null,
    gtdate = null,
    ltdate = null,
    patient = null,
}) => {
    const { userInfo } = useSelector((state) => state.userLogin);
    return useQuery({
        queryKey: [
            "appointments",
            userInfo,
            page,
            limit,
            status,
            gtdate,
            ltdate,
            patient,
        ], // Unique cache key
        queryFn: listAppointment,
        enabled: !!userInfo?._id,
        staleTime: 5 * 60 * 1000, // Cache for 5 minutes
        retry: 1,
    });
};

export const useInfiniteAppointments = ({
    page = 1,
    limit = 10,
    status = null,
    date = null,
    patient = null,
}) => {
    const { userInfo } = useSelector((state) => state.userLogin);

    return useInfiniteQuery(
        ["appointments", userInfo, page, limit, status, date, patient], // Unique cache key
        async ({ pageParam = page }) => {
            // Pass the pageParam correctly to fetchVacAppointments
            const response = await fetchVacAppointments({
                userInfo, // Pass userInfo directly
                page: pageParam, // Pass the current page number
                limit,
                status,
                date,
                patient,
            });
            return response; // Return the fetched response data
        },
        {
            enabled: !!userInfo?._id, // Only fetch if user is logged in
            staleTime: 5 * 60 * 1000, // Cache for 5 minutes
            retry: 1, // Retry once on failure
            getNextPageParam: (lastPage) => {
                // If current page is less than total pages, return next page number
                return lastPage.currentPage < lastPage.totalPages
                    ? lastPage.currentPage + 1
                    : undefined;
            },
        }
    );
};

const updateAppointment = async ({ _id, ...appVacc }, user) => {
    const config = {
        headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${user.token}`,
        },
    };

    const { data } = await axios.put(
        `${API}/vaccine-app-update/${_id}/`,
        appVacc,
        config
    );

    return data;
};

export const useUpdateAppointment = () => {
    const queryClient = useQueryClient();
    const { userInfo } = useSelector((state) => state.userLogin);
    return useMutation({
        mutationFn: ({ _id, ...appVacc }) =>
            updateAppointment({ _id, ...appVacc }, { token: userInfo?.token }),
        onSuccess: () => {
            queryClient.invalidateQueries(["appointments"]);
        },
    });
};

export const useInvalidateAppointments = () => {
    const queryClient = useQueryClient();

    return () => {
        queryClient.invalidateQueries(["appointments"]);
    };
};

const fetchVacAppDetails = async (id, token) => {
  const { data } = await axios.get(`${API}/vaccine-app-detail/${id}`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  return data;
};

export const useVacAppDetails = (id) => {
  const { userInfo } = useSelector((state) => state.userLogin);

  return useQuery({
    queryKey: ['vaccineAppointmentDetail', id],
    queryFn: () => fetchVacAppDetails(id, userInfo.token),
    enabled: !!userInfo?.token && !!id, // Prevent running without token or id
    retry: 1,
    staleTime: 5 * 60 * 1000, // optional: cache for 5 min
  });
};